import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { ExpiryDateSelector } from "PFApp/components/activity_form/form_selectors/expiry_date_selector";
import { ExpiryDateModal, PostUntilData } from "PFApp/components/expiry_date_modal";
import {
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline } from "PFComponents/containers";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { PostUntilChangeReason } from "PFTypes";
import { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ExpiryDateReadOnly } from "./expiry_date_read_only";

export const ExpiryDate = () => {
  const [modalOpened, setModalOpened] = useState(false);
  const { formatISODate } = useDateFormatter();
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();
  const template = useActivityFormStore((state) => state.template);
  const qaIdPrefix = useActivityFormStore((state) => state.qaIdPrefix);
  const { isEdit, isDraft } = useActivityFormStore((state) => state.activityStatus);
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);
  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const [postUntil, postUntilChangeReason] = useWatch({
    name: [FIELD_POST_UNTIL_KEY, FIELD_POST_UNTIL_CHANGE_REASON_KEY]
  });

  const postUntilReasonChangeExists = !isEmpty(template?.localized_post_until_change_reasons);

  const handleChange = ({ date, reason }: PostUntilData) => {
    setValue(FIELD_POST_UNTIL_KEY, date, { shouldDirty: true });
    setValue(FIELD_POST_UNTIL_CHANGE_REASON_KEY, reason as PostUntilChangeReason, { shouldDirty: true }); // handleChange is used only in case reasons are defined so it means reason.text is always present at this point
  };
  const handleClose = () => setModalOpened(false);

  const required = isFieldRequired({ name: FIELD_POST_UNTIL_KEY });

  return (
    <>
      {isEdit && !!postUntil && postUntilReasonChangeExists ? (
        <Inline>
          <ExpiryDateReadOnly postUntil={postUntil} reason={postUntilChangeReason} />
          <ActionIcon name="edit" size="sm" onClick={() => setModalOpened(true)} />
        </Inline>
      ) : (
        <Controller
          name={FIELD_POST_UNTIL_KEY}
          control={control}
          rules={{
            validate: (value: string) => {
              if (required && !value) {
                return t("errors.cantBeBlank");
              }
              if ((submittedAsDraft || isDraft) && value && new Date(value) < new Date()) {
                return t("errors.shouldNotBeExpired");
              }
              return true;
            }
          }}
          render={({ field }) => (
            <ExpiryDateSelector
              label={t("sections.basicInformation.expiryDate.title")}
              value={field.value}
              qaId={`${qaIdPrefix}-post_until`}
              error={get(errors, field.name)?.message as string}
              onChange={field.onChange}
              required={required}
            />
          )}
        />
      )}
      {modalOpened && (
        <ExpiryDateModal
          title={t("sections.basicInformation.expiryDate.modal.title")}
          reasonLabel={t("sections.basicInformation.expiryDate.modal.reasonLabel")}
          reason={postUntilChangeReason}
          date={formatISODate(postUntil)}
          template={template}
          onChange={handleChange}
          onClose={handleClose}
        />
      )}
    </>
  );
};
